import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { useAuthModal } from '../../features/auth/AuthModalContext';
import { useI18n } from '../../features/i18n';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/about — mission + trust page. Tells the origin story (why this
 * exists), lists the core values, and closes with a CTA. No team bios yet
 * since we don't have headshots — placeholder-free version.
 */

const EASE = [0.22, 1, 0.36, 1];

// Tone + icon are visual tokens — those stay module-scoped. Strings are
// pulled fresh from t() inside the component so switching language
// re-renders instantly.
const VALUE_VISUALS = [
  { id: 'v1', tone: 'blue',   icon: 'shield' },
  { id: 'v2', tone: 'green',  icon: 'handshake' },
  { id: 'v3', tone: 'purple', icon: 'people' },
  { id: 'v4', tone: 'orange', icon: 'spark' },
];

// Years are pure data, copy pulls from translations at render time.
const MILESTONE_YEARS = [
  { id: 'ms1', year: '2024' },
  { id: 'ms2', year: '2025' },
  { id: 'ms3', year: '2026' },
];

const TONE_COLORS = {
  blue:   { bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  solid: 'var(--cwm-blue-deep)',  glow: 'rgba(15,127,232,0.28)' },
  green:  { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', solid: 'var(--cwm-green)',      glow: 'rgba(22,128,60,0.26)' },
  purple: { bg: '#F1E8FE',                fg: '#5B21B6',               solid: '#7C3AED',               glow: 'rgba(124,58,237,0.26)' },
  orange: { bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', solid: 'var(--cwm-amber)',      glow: 'rgba(180,83,9,0.26)' },
};

export default function UnifiedAbout() {
  const navigate = useNavigate();
  const { openAuth } = useAuthModal();
  const { t } = useI18n();

  // Hydrate the visual templates with translated copy each render.
  const values     = VALUE_VISUALS.map((v) => ({ ...v, title: t(`about.${v.id}_title`), body: t(`about.${v.id}_body`) }));
  const milestones = MILESTONE_YEARS.map((m) => ({ ...m, label: t(`about.${m.id}_label`), body: t(`about.${m.id}_body`) }));

  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 980, margin: '0 auto', padding: '36px 20px 80px', position: 'relative', zIndex: 1 }}>

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.56, ease: EASE }}
          style={{ textAlign: 'center', marginBottom: 36 }}
        >
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 14 }}>
            <HeartIcon size={12} /> {t('about.eyebrow')}
          </div>
          <h1 style={{ fontSize: 48, fontWeight: 900, letterSpacing: '-0.028em', color: 'var(--cwm-ink)', margin: '0 0 14px', lineHeight: 1.05 }}>
            {t('about.headline_l1')} <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>{t('about.headline_emphasis')}</em>.
          </h1>
          <p style={{ maxWidth: 640, margin: '0 auto', fontSize: 16, fontWeight: 500, color: 'var(--cwm-gray-700)', lineHeight: 1.6 }}>
            {t('about.sub')}
          </p>
        </motion.section>

        {/* Origin story */}
        <motion.section
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.56, delay: 0.12, ease: EASE }}
          className="cwm-sheet"
          style={{ padding: '32px 30px', marginBottom: 30 }}
        >
          <div className="cwm-eyebrow" style={{ color: 'var(--cwm-blue-deep)', marginBottom: 10 }}>
            {t('about.story_eyebrow')}
          </div>
          <h2 style={{ fontSize: 26, fontWeight: 900, letterSpacing: '-0.02em', color: 'var(--cwm-ink)', margin: '0 0 14px' }}>
            {t('about.story_title')}
          </h2>
          <p style={{ margin: '0 0 14px', fontSize: 15.5, lineHeight: 1.7, color: 'var(--cwm-gray-700)', fontWeight: 500 }}>
            {t('about.story_p1')}
          </p>
          <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.7, color: 'var(--cwm-gray-700)', fontWeight: 500 }}>
            {t('about.story_p2')}
          </p>
        </motion.section>

        {/* Values */}
        <div style={{ marginBottom: 36 }}>
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.46, delay: 0.2, ease: EASE }}
            style={{ textAlign: 'center', marginBottom: 22 }}
          >
            <div className="cwm-eyebrow">{t('about.values_eyebrow')}</div>
            <h2 style={{ fontSize: 28, fontWeight: 900, letterSpacing: '-0.02em', color: 'var(--cwm-ink)', margin: '4px 0 0' }}>
              {t('about.values_title')}
            </h2>
          </motion.div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {values.map((v, i) => (
              <ValueCard key={v.id} value={v} index={i} />
            ))}
          </div>
        </div>

        {/* Milestones */}
        <motion.section
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.56, delay: 0.3, ease: EASE }}
          className="cwm-sheet"
          style={{ padding: '28px 26px', marginBottom: 30 }}
        >
          <div className="cwm-eyebrow" style={{ color: 'var(--cwm-blue-deep)', marginBottom: 8 }}>{t('about.timeline_eyebrow')}</div>
          <h2 style={{ fontSize: 22, fontWeight: 900, letterSpacing: '-0.015em', color: 'var(--cwm-ink)', margin: '0 0 20px' }}>
            {t('about.timeline_title')}
          </h2>
          <div style={{ display: 'grid', gap: 16 }}>
            {milestones.map((m, i) => (
              <motion.div
                key={m.year}
                initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.46, delay: 0.36 + i * 0.08, ease: EASE }}
                style={{ display: 'grid', gridTemplateColumns: '88px 1fr', gap: 16, alignItems: 'start' }}
              >
                <div style={{
                  padding: '8px 12px', borderRadius: 12,
                  background: 'linear-gradient(160deg, var(--cwm-blue-pale), #fff)',
                  border: '1.5px solid var(--cwm-blue-light)',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--cwm-gray-500)', letterSpacing: '0.06em' }}>
                    {m.label.toUpperCase()}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: 'var(--cwm-blue-deep)', letterSpacing: '-0.01em' }}>
                    {m.year}
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--cwm-gray-700)', fontWeight: 500, paddingTop: 4 }}>
                  {m.body}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Closing CTA */}
        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.56, delay: 0.4, ease: EASE }}
          style={{
            position: 'relative',
            padding: '36px 28px',
            borderRadius: 28,
            background: 'linear-gradient(160deg, #BCE7FF 0%, #5BC2FF 45%, #1E9BFF 100%)',
            boxShadow: '0 24px 56px rgba(13,71,161,0.24)',
            color: '#fff',
            textAlign: 'center',
            overflow: 'hidden',
          }}
        >
          <div aria-hidden style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(circle, rgba(255,255,255,0.22) 0, rgba(255,255,255,0.22) 1.5px, transparent 1.6px)',
            backgroundSize: '22px 22px', opacity: 0.3,
          }}/>
          <div style={{ position: 'relative' }}>
            <h2 style={{ fontSize: 30, fontWeight: 900, letterSpacing: '-0.02em', margin: '0 0 10px' }}>
              {t('about.cta_headline_l1')} <em style={{ fontStyle: 'italic' }}>{t('about.cta_headline_emphasis')}</em>.
            </h2>
            <p style={{ margin: '0 auto 22px', maxWidth: 500, fontSize: 15, fontWeight: 600, opacity: 0.94 }}>
              {t('about.cta_body')}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={() => openAuth('signup')}
                style={{
                  padding: '14px 32px', borderRadius: 999,
                  background: '#fff', color: 'var(--cwm-blue-deep)',
                  border: 'none', cursor: 'pointer',
                  fontFamily: 'var(--cwm-font)', fontSize: 15, fontWeight: 900,
                  boxShadow: '0 10px 24px rgba(10,37,64,0.2)',
                }}>
                {t('about.cta_primary')}
              </button>
              <button
                onClick={() => navigate('/unified/how-it-works')}
                style={{
                  padding: '14px 26px', borderRadius: 999,
                  background: 'rgba(255,255,255,0.14)', color: '#fff',
                  border: '1.5px solid rgba(255,255,255,0.4)', cursor: 'pointer',
                  fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 800,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  backdropFilter: 'blur(6px)',
                }}>
                {t('about.cta_secondary')} <ArrowIcon size={12} />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AppShell>
  );
}

function ValueCard({ value, index }) {
  const tone = TONE_COLORS[value.tone] || TONE_COLORS.blue;
  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.46, delay: 0.26 + index * 0.06, ease: EASE }}
      whileHover={{ y: -3 }}
      className="cwm-sheet"
      style={{ padding: 22 }}
    >
      <div style={{
        width: 48, height: 48, borderRadius: 14,
        display: 'grid', placeItems: 'center',
        background: `linear-gradient(160deg, #fff, ${tone.bg})`,
        border: `1.5px solid ${tone.solid}33`,
        color: tone.solid,
        boxShadow: `0 6px 16px ${tone.glow}`,
        marginBottom: 14,
      }}>
        <ValueIcon name={value.icon} size={22} />
      </div>
      <h3 style={{ fontSize: 17, fontWeight: 900, letterSpacing: '-0.01em', color: 'var(--cwm-ink)', margin: '0 0 8px' }}>
        {value.title}
      </h3>
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: 'var(--cwm-gray-700)', fontWeight: 500 }}>
        {value.body}
      </p>
    </motion.article>
  );
}

function HeartIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 21s-7-5.5-7-11a4 4 0 017-2.6A4 4 0 0119 10c0 5.5-7 11-7 11z"/></svg>; }
function ArrowIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>; }

function ValueIcon({ name, size = 22 }) {
  const paths = {
    shield:    'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
    handshake: 'M14 9l-4 4-2-2-4 4 6 6 4-4 2 2 4-4M12 12l4-4M8 16l-4-4',
    people:    'M9 11a3 3 0 100-6 3 3 0 000 6zM17 12a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM3 19c0-3 3-5 6-5s6 2 6 5M16 19c0-2 1.5-4 4-4',
    spark:     'M12 2l2 6 6 2-6 2-2 6-2-6-6-2 6-2z',
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill={name === 'spark' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={paths[name] || paths.shield}/></svg>;
}
